import logger from "../../../lib/logger";
import { getProductTileId } from "../../../helpers/getProductTileId";

export const createIntersectionObserver = (callback, observerOptions) => {
  try {
    return new IntersectionObserver(callback, observerOptions);
  } catch (error) {
    const errorText =
      "Failed to initialize IntersectionObserver in ListingPage";
    logger.warn(new Error(`${errorText}: ${error.message}`));

    return null;
  }
};

export const checkIntersectionObserverSupport = () => {
  if (typeof IntersectionObserver === "undefined") {
    const errorText =
      "IntersectionObserver not supported in ListingPage. Please check browser compatibility.";
    logger.warn(new Error(errorText));

    return false;
  }

  return true;
};

export const createIntersectionCallback =
  ({ visibleRowsRef, firedIdsRef }) =>
  (entries) => {
    entries.forEach((entry) => {
      const id = entry.target.id;

      if (entry.isIntersecting && !firedIdsRef.current.has(id)) {
        visibleRowsRef.current.add(id);
      } else {
        visibleRowsRef.current.delete(id);
      }
    });
  };

export const getAbsoluteProductIndex = (
  pageNumber,
  onPageProductIndex,
  pageSize,
) => (pageNumber - 1) * pageSize + onPageProductIndex;

export const setupRowObservers = ({
  listRef,
  products,
  columnCount,
  observerRef,
}) => {
  if (!listRef.current || !observerRef.current) return;

  const expectedProductIds = new Set(
    products
      .map((item) => item.product?.tileId)
      .filter(Boolean)
      .map((tileId) => getProductTileId(tileId)),
  );

  if (!expectedProductIds.size || columnCount < 1) return;

  const renderedProductElements = Array.from(listRef.current.children).filter(
    (element) => expectedProductIds.has(element.id),
  );

  renderedProductElements.forEach((element, productIndex) => {
    if (productIndex % columnCount === 0) {
      observerRef.current.observe(element);
    }
  });
};

export const setupScrollInactivityListener = ({
  onScrollInactive,
  inactivityDelay,
}) => {
  let scrollInactiveTimerId = null;

  const handleScrollInactivity = () => {
    if (scrollInactiveTimerId) {
      clearTimeout(scrollInactiveTimerId);
    }

    scrollInactiveTimerId = setTimeout(() => {
      onScrollInactive();
    }, inactivityDelay);
  };

  const handleScroll = () => {
    handleScrollInactivity();
  };

  window.addEventListener("scroll", handleScroll, { passive: true });

  // Initial inactivity trigger on mount
  handleScrollInactivity();

  // Return cleanup function
  return () => {
    window.removeEventListener("scroll", handleScroll);
    if (scrollInactiveTimerId) {
      clearTimeout(scrollInactiveTimerId);
    }
  };
};
